import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Award, AlertCircle, BookOpen,
  Target, Brain, ArrowLeft, Loader2, Star, BarChart2, Lightbulb,
  RefreshCw, ChevronDown, ChevronUp, Calendar, Trophy
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import api from '../../api';

// ── Helpers ───────────────────────────────────────────────────────────────────
const pctColor = p =>
  p >= 75 ? '#10b981' : p >= 60 ? '#3b82f6' : p >= 45 ? '#f59e0b' : '#ef4444';

const trendIcon = t =>
  t === 'up' || t === 'improving'
    ? <TrendingUp className="w-4 h-4 text-emerald-500" />
    : t === 'down' || t === 'declining'
    ? <TrendingDown className="w-4 h-4 text-rose-500" />
    : <Minus className="w-4 h-4 text-slate-400" />;

const trendLabel = t =>
  t === 'up' || t === 'improving' ? 'Improving'
  : t === 'down' || t === 'declining' ? 'Declining'
  : 'Stable';

// ── Stat Card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className={`bg-white rounded-2xl border border-slate-100 p-4 shadow-sm`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center mb-3 ${color}`}>
        <Icon className="w-4.5 h-4.5 text-white w-[18px] h-[18px]" />
      </div>
      <div className="text-2xl font-black text-slate-800">{value}</div>
      <div className="text-xs font-bold text-slate-600 mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── AI Plan Card ──────────────────────────────────────────────────────────────
function AiPlanCard({ plan, onGenerate, loading }) {
  const [expanded, setExpanded] = useState(null);

  if (!plan) return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-5 text-white"
      data-testid="ai-plan-card">
      <div className="flex items-center gap-2 mb-2">
        <Brain className="w-5 h-5" />
        <span className="font-bold">AI Improvement Plan</span>
      </div>
      <p className="text-blue-100 text-sm mb-4">
        Let AI analyze your performance and create a personalized 4-week study plan.
      </p>
      <button onClick={onGenerate} disabled={loading}
        data-testid="generate-ai-plan-btn"
        className="w-full py-2.5 bg-white text-blue-700 rounded-xl font-bold text-sm
          hover:bg-blue-50 transition-all flex items-center justify-center gap-2 disabled:opacity-60">
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
        {loading ? 'Generating Plan…' : 'Generate My Study Plan'}
      </button>
    </div>
  );

  return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl p-5 text-white"
      data-testid="ai-plan-card-loaded">
      <div className="flex items-center gap-2 mb-1">
        <Brain className="w-5 h-5" />
        <span className="font-bold">AI Improvement Plan</span>
      </div>
      <p className="text-blue-100 text-sm mb-4">{plan.summary}</p>

      {/* Focus Areas */}
      {plan.focus_areas?.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-bold text-blue-200 uppercase tracking-wider mb-2">Focus Areas</p>
          <div className="flex flex-wrap gap-1.5">
            {plan.focus_areas.map((a, i) => (
              <span key={i} className="px-2.5 py-1 bg-white/15 rounded-full text-xs font-medium">{a}</span>
            ))}
          </div>
        </div>
      )}

      {/* Weekly Plan */}
      {plan.weekly_plan?.length > 0 && (
        <div className="space-y-2 mb-4">
          <p className="text-xs font-bold text-blue-200 uppercase tracking-wider mb-2">4-Week Plan</p>
          {plan.weekly_plan.map((w, i) => (
            <div key={i} className="bg-white/10 rounded-xl overflow-hidden">
              <button onClick={() => setExpanded(expanded === i ? null : i)}
                className="w-full text-left px-3 py-2.5 flex items-center justify-between">
                <span className="font-semibold text-sm">Week {w.week}: {w.focus}</span>
                {expanded === i ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {expanded === i && (
                <div className="px-3 pb-3 space-y-1">
                  {(w.activities || []).map((a, j) => (
                    <div key={j} className="text-xs text-blue-100 flex items-start gap-1.5">
                      <span className="text-blue-300 mt-0.5 flex-shrink-0">•</span> {a}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Encouragement */}
      {plan.encouragement && (
        <div className="bg-white/10 rounded-xl px-3 py-2.5 text-sm text-blue-100 flex items-start gap-2">
          <Star className="w-4 h-4 text-yellow-300 flex-shrink-0 mt-0.5" />
          {plan.encouragement}
        </div>
      )}

      <button onClick={onGenerate} disabled={loading}
        data-testid="regenerate-ai-plan-btn"
        className="mt-3 w-full py-2 bg-white/15 text-white rounded-xl font-semibold text-xs
          hover:bg-white/20 transition-all flex items-center justify-center gap-1.5 disabled:opacity-50">
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        Refresh Plan
      </button>
    </div>
  );
}

// ── Subject Row ───────────────────────────────────────────────────────────────
function SubjectRow({ s, classAvg }) {
  return (
    <div className="flex items-center gap-3 py-3 border-b border-slate-100 last:border-0">
      <div className="w-8 h-8 rounded-xl flex-shrink-0 flex items-center justify-center"
        style={{ background: `${pctColor(s.avg_pct)}20` }}>
        <BookOpen className="w-4 h-4" style={{ color: pctColor(s.avg_pct) }} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-semibold text-sm text-slate-800 truncate">{s.subject}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            {trendIcon(s.trend)}
            <span className="font-black text-sm" style={{ color: pctColor(s.avg_pct) }}>
              {s.avg_pct}%
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 bg-slate-100 rounded-full h-1.5">
            <div className="h-1.5 rounded-full transition-all" style={{
              width: `${Math.min(100, s.avg_pct)}%`,
              background: pctColor(s.avg_pct)
            }} />
          </div>
          <span className="text-[11px] text-slate-400 flex-shrink-0">{s.attempts} attempts</span>
        </div>
        {classAvg && (
          <div className="text-[11px] mt-0.5 text-slate-400">
            Class avg: <span className="font-semibold" style={{ color: pctColor(classAvg) }}>{classAvg}%</span>
            {s.avg_pct > classAvg
              ? <span className="text-emerald-500 ml-1">+{(s.avg_pct - classAvg).toFixed(1)} above</span>
              : s.avg_pct < classAvg
              ? <span className="text-rose-500 ml-1">{(s.avg_pct - classAvg).toFixed(1)} below</span>
              : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AcademicGrowthPage() {
  const navigate = useNavigate();
  const [data,       setData]      = useState(null);
  const [aiPlan,     setAiPlan]    = useState(null);
  const [loading,    setLoading]   = useState(true);
  const [planLoading,setPlanLoading] = useState(false);
  const [activeTab,  setActiveTab] = useState('overview'); // overview | trends | compare

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/growth/dashboard');
      if (res.success) setData(res.data);
    } catch { toast.error('Failed to load growth data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const generatePlan = async () => {
    setPlanLoading(true);
    try {
      const res = await api.post('/growth/ai-plan');
      if (res.success) {
        setAiPlan(res.data);
        toast.success('AI study plan generated!');
      }
    } catch { toast.error('Failed to generate plan'); }
    finally { setPlanLoading(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="text-center">
        <Loader2 className="w-10 h-10 text-[#2563eb] animate-spin mx-auto mb-3" />
        <p className="text-slate-500 text-sm">Loading your academic growth data…</p>
      </div>
    </div>
  );

  if (!data || data.total_quiz_attempts === 0) return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/student/dashboard')}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <h1 className="text-xl font-black text-slate-800">Academic Growth</h1>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center shadow-sm">
        <BarChart2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="font-bold text-slate-700">No data yet</p>
        <p className="text-sm text-slate-400 mt-1 mb-5">
          Complete some quiz sessions to see your academic growth analytics.
        </p>
        <button onClick={() => navigate('/student/quiz')}
          className="px-5 py-2.5 bg-[#2563eb] text-white rounded-xl font-semibold text-sm hover:bg-blue-700 transition-all">
          Go to Quiz Zone
        </button>
      </div>
    </div>
  );

  const classAvgMap = Object.fromEntries(
    (data.class_comparison || []).map(c => [c.subject, c.class_avg])
  );

  // Prepare trend chart data (last 30 quiz attempts as overview)
  const trendData = (data.subject_stats || [])
    .filter(s => s.recent_scores?.length >= 2)
    .slice(0, 5)
    .map(s => s.recent_scores.map((score, i) => ({ name: `Q${i + 1}`, [s.subject]: score })));

  const chartSubjects = (data.subject_stats || []).slice(0, 6).map(s => ({
    subject: s.subject.length > 12 ? s.subject.slice(0, 12) + '…' : s.subject,
    avg: s.avg_pct,
    best: s.best_pct,
  }));

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="academic-growth-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate('/student/dashboard')}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center shadow-md">
          <TrendingUp className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-black text-slate-800">Academic Growth</h1>
          <p className="text-xs text-slate-500">Tracking {data.class_name}</p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Overall Avg" value={`${data.overall_avg}%`}
          sub={trendLabel(data.trend)} icon={BarChart2} color="bg-blue-600"
          data-testid="stat-overall-avg" />
        <StatCard label="Strongest" value={data.strongest_subject?.split(' ')[0] || '—'}
          sub={data.strongest_subject} icon={Award} color="bg-emerald-600" />
        <StatCard label="Needs Work" value={data.weakest_subject?.split(' ')[0] || '—'}
          sub={data.weakest_subject} icon={AlertCircle} color="bg-rose-500" />
        <StatCard label="Sessions" value={data.total_quiz_attempts}
          sub="quiz attempts" icon={Target} color="bg-purple-600" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-slate-100 rounded-2xl p-1 mb-6">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'subjects', label: 'By Subject' },
          { key: 'plan',     label: 'AI Plan' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            data-testid={`growth-tab-${t.key}`}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === t.key ? 'bg-white shadow-sm text-[#2563eb]' : 'text-slate-500 hover:text-slate-700'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* Subject Bar Chart */}
          {chartSubjects.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm"
              data-testid="subject-bar-chart">
              <h3 className="font-bold text-slate-800 mb-4 text-sm flex items-center gap-2">
                <BarChart2 className="w-4 h-4 text-blue-600" /> Subject Performance
              </h3>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartSubjects} margin={{ left: -20, right: 5, top: 5, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="subject" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                  <Tooltip formatter={v => `${v}%`} />
                  <Bar dataKey="avg" name="Avg Score" radius={[4, 4, 0, 0]}>
                    {chartSubjects.map((s, i) => (
                      <Cell key={i} fill={pctColor(s.avg)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Weak Topics */}
          {data.weak_topics?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5"
              data-testid="weak-topics-section">
              <h3 className="font-bold text-amber-800 mb-3 text-sm flex items-center gap-2">
                <Target className="w-4 h-4" /> Topics Needing Practice
              </h3>
              <div className="flex flex-wrap gap-2">
                {data.weak_topics.map((t, i) => (
                  <span key={i} className="px-3 py-1.5 bg-amber-100 border border-amber-300 rounded-full text-xs font-semibold text-amber-700">
                    {t}
                  </span>
                ))}
              </div>
              <button onClick={() => navigate('/student/exam-prep')}
                data-testid="practice-weak-topics-link"
                className="mt-4 w-full py-2.5 bg-amber-500 text-white rounded-xl text-sm font-bold hover:bg-amber-600 transition-all flex items-center justify-center gap-2">
                <Brain className="w-4 h-4" /> Practice These in Exam Prep
              </button>
            </div>
          )}

          {/* Trend */}
          <div className={`rounded-2xl p-4 border flex items-center gap-3 ${
            data.trend === 'improving' ? 'bg-emerald-50 border-emerald-200' :
            data.trend === 'declining' ? 'bg-rose-50 border-rose-200' :
            'bg-slate-50 border-slate-200'
          }`} data-testid="trend-indicator">
            {trendIcon(data.trend)}
            <div>
              <p className="font-bold text-sm text-slate-800">
                {data.trend === 'improving' ? 'You\'re improving!' :
                 data.trend === 'declining' ? 'Performance dipping' : 'Consistent performance'}
              </p>
              <p className="text-xs text-slate-500">
                Recent avg: {data.recent_avg}% vs all-time: {data.overall_avg}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* By Subject Tab */}
      {activeTab === 'subjects' && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
          data-testid="subjects-tab">
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 text-sm">All Subjects</h3>
            <span className="text-xs text-slate-400">{(data.subject_stats || []).length} subjects tracked</span>
          </div>
          <div className="px-5 divide-y divide-slate-50">
            {(data.subject_stats || []).map(s => (
              <SubjectRow key={s.subject} s={s} classAvg={classAvgMap[s.subject]} />
            ))}
          </div>
          {(data.subject_stats || []).length === 0 && (
            <div className="p-8 text-center text-slate-400 text-sm">Complete quizzes to see per-subject stats</div>
          )}
        </div>
      )}

      {/* AI Plan Tab */}
      {activeTab === 'plan' && (
        <div className="space-y-4">
          <AiPlanCard plan={aiPlan} onGenerate={generatePlan} loading={planLoading} />

          {/* Study Tips from plan */}
          {aiPlan?.study_tips?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
              <h3 className="font-bold text-slate-800 mb-3 text-sm flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" /> Study Tips
              </h3>
              <div className="space-y-2.5">
                {aiPlan.study_tips.map((tip, i) => (
                  <div key={i} className="flex items-start gap-2.5 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!aiPlan && (
            <div className="bg-white rounded-2xl border border-slate-100 p-6 text-center shadow-sm">
              <Lightbulb className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                Generate your AI plan above to get personalized study tips and a week-by-week schedule.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
